import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { Input } from '@neo/primitives/input'
import { PlusIcon, Trash2 } from 'lucide-react'
import React from 'react'
import type { AgentSettingsFormValues } from '../../config/base.config'

interface STTConfigProps {
  form: GenericFormType<AgentSettingsFormValues>
}

/**
 * Speech-to-Text Configuration Component
 *
 * Provides a list of text inputs for keyterms that improve Deepgram STT accuracy.
 * Keyterms help recognize brand names, technical terms, and domain-specific vocabulary.
 *
 * Note: Keyterm prompting only works with Deepgram Nova-3 and Flux models.
 * Limit: 500 tokens total (~20-50 keyterms recommended for best results)
 */
export const STTConfig = React.memo(({ form }: STTConfigProps) => {
  const keyterms = form.useStore(
    (state) => state.values.voice_ai_config?.speech_to_text?.keyterms || [],
  )

  const handleAdd = () => {
    form.setFieldValue('voice_ai_config.speech_to_text.keyterms', [...keyterms, ''])
  }

  const handleRemove = (index: number) => {
    form.setFieldValue(
      'voice_ai_config.speech_to_text.keyterms',
      keyterms.filter((_, i) => i !== index),
    )
  }

  const handleChange = (index: number, val: string) => {
    const newTerms = [...keyterms]
    newTerms[index] = val
    form.setFieldValue('voice_ai_config.speech_to_text.keyterms', newTerms)
  }

  // Remove empty keyterms when input loses focus
  const handleBlur = () => {
    const filtered = keyterms.filter((t) => t.trim() !== '')
    if (filtered.length !== keyterms.length) {
      form.setFieldValue('voice_ai_config.speech_to_text.keyterms', filtered)
    }
  }

  const MAX_KEYTERMS = 50

  return (
    <div className="flex flex-col gap-2">
      <div className="text-p-500 text-foreground">Keyterm prompting</div>
      <p className="text-s-400 text-secondary">
        Define Keyterms, like product names or proper nouns, to boost the model&apos;s transcription
        accuracy on calls.
      </p>

      <div className="flex flex-col gap-4 rounded-md border border-border bg-bg2 p-4">
        {keyterms.map((term, idx) => (
          <div key={idx} className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <span className="text-s-500 text-foreground">Keyterm {idx + 1}</span>
              <Button
                variant="ghostDestructive"
                size="icon"
                className="h-6 w-6"
                onClick={() => handleRemove(idx)}
                type="button"
              >
                <Trash2 size={14} />
              </Button>
            </div>
            <Input
              placeholder="Enter word or sentence"
              value={term}
              onChange={(e) => handleChange(idx, e.target.value)}
              onBlur={handleBlur}
              data-testid={`input-keyterm-${idx}`}
            />
          </div>
        ))}
        <Button
          variant="ghost"
          className="w-fit justify-start px-2 text-brand-text hover:bg-hv1 hover:text-brand-text"
          onClick={handleAdd}
          type="button"
          disabledReason={`Maximum ${MAX_KEYTERMS} keyterms allowed`}
          disabled={keyterms.length >= MAX_KEYTERMS}
        >
          <PlusIcon size={16} className="mr-2" />
          Add keyterm
        </Button>
      </div>
    </div>
  )
})

STTConfig.displayName = 'STTConfig'
